<template>
  <div class="editor-section">
    <p class="section-desc">Flex Carousel 允許多個 Bubble 橫向滑動顯示，最多 12 個。每張卡片可選擇不同版型；點標題列左側箭頭可收合／展開該卡。</p>

    <!-- Bubble 列表 -->
    <div class="bubbles-list">
      <div v-for="(bubble, idx) in bubbles" :key="idx" class="bubble-card" :class="{ 'is-collapsed': !expanded[idx] }">
        <div class="bubble-header">
          <div class="bubble-header-left">
            <button
              type="button"
              class="icon-btn collapse-toggle"
              :title="expanded[idx] ? '收合' : '展開'"
              :aria-expanded="expanded[idx]"
              @click="toggleCollapse(idx)"
            >
              <i :class="expanded[idx] ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
            </button>
            <span class="bubble-title">卡片 {{ idx + 1 }}</span>
          </div>
          <div class="bubble-actions">
            <button
              type="button"
              class="icon-btn"
              title="上移"
              :disabled="idx === 0"
              @click="moveBubble(idx, -1)"
            ><i class="ri-arrow-up-s-line"></i></button>
            <button
              type="button"
              class="icon-btn"
              title="下移"
              :disabled="idx === bubbles.length - 1"
              @click="moveBubble(idx, 1)"
            ><i class="ri-arrow-down-s-line"></i></button>
            <button
              type="button"
              class="icon-btn danger"
              title="刪除"
              @click="removeBubble(idx)"
              :disabled="bubbles.length <= 1"
            ><i class="ri-delete-bin-line"></i></button>
          </div>
        </div>

        <!-- 版型選擇（每張卡片獨立） -->
        <div v-show="expanded[idx]" class="bubble-preset-selector">
          <label class="form-label">版型</label>
          <div class="preset-chips">
            <button
              v-for="p in presetOptions"
              :key="p.key"
              type="button"
              class="preset-chip"
              :class="{ active: bubble.preset === p.key }"
              @click="setBubblePreset(idx, p.key)"
            >
              <i :class="presetIcons[p.key] || 'ri-code-s-slash-line'"></i>
              <span>{{ p.name }}</span>
            </button>
          </div>
        </div>

        <!-- 預設版型表單 -->
        <FlexPresetForm
          v-if="expanded[idx] && bubble.preset && bubble.preset !== 'custom'"
          :preset-key="bubble.preset"
          :schema="getSchema(bubble.preset)"
          :modelValue="bubble.fields || {}"
          @update:modelValue="updateBubbleFields(idx, $event)"
        />

        <!-- 自訂 JSON -->
        <div v-else-if="expanded[idx] && bubble.preset === 'custom'" class="custom-json">
          <label class="form-label required-label">Flex Bubble JSON</label>
          <textarea
            class="form-control code-textarea"
            :value="bubble.json || ''"
            @input="updateBubbleJson(idx, $event.target.value)"
            rows="8"
            placeholder='{"type":"bubble","body":{...}}'
          ></textarea>
          <span v-if="bubble._jsonError" class="error-text">{{ bubble._jsonError }}</span>
        </div>
      </div>

      <button v-if="bubbles.length < 12" type="button" class="btn-add" @click="addBubble">
        <i class="ri-add-circle-line"></i> 新增卡片（{{ bubbles.length }}/12）
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import FlexPresetForm from './FlexPresetForm.vue';
import { lineMsgTemplateService } from '@/api/modules/lineMsgTemplates.js';

const props = defineProps({ modelValue: { type: Object, required: true } });
const emit = defineEmits(['update:modelValue']);

const allPresets = ref([]);

const presetIcons = {
  product_card: 'ri-shopping-bag-line',
  coupon_card: 'ri-coupon-3-line',
  event_announce: 'ri-calendar-event-line',
  order_confirm: 'ri-file-list-3-line',
  member_welcome: 'ri-user-heart-line',
  image_text: 'ri-image-2-line',
  image_banner: 'ri-gallery-line',
  template_card: 'ri-layout-4-line',
  business_card: 'ri-contacts-book-line',
  card_portrait_full: 'ri-id-card-line',
  card_avatar_center: 'ri-account-circle-line',
  card_company_hero: 'ri-building-2-line',
  card_gradient_banner: 'ri-color-filter-line',
  card_minimal_text: 'ri-text',
  custom: 'ri-code-s-slash-line'
};

const presetOptions = ref([]);

function initBubbles() {
  const tf = props.modelValue.templateFields;
  if (Array.isArray(tf) && tf.length > 0) {
    // New format: each item has { preset, fields } or old format: each item is just fields
    if (tf[0] && tf[0].preset) {
      return tf.map(b => ({ ...b }));
    }
    // Old format: all use same preset
    const singlePreset = props.modelValue.flexPreset || '';
    return tf.map(fields => ({ preset: singlePreset, fields: { ...fields } }));
  }
  return [{ preset: '', fields: {} }];
}

const bubbles = ref(initBubbles());
/** 與 bubbles 同索引：true 為展開、false 為收合 */
const expanded = ref(bubbles.value.map(() => true));

function toggleCollapse(idx) {
  expanded.value[idx] = !expanded.value[idx];
}

onMounted(async () => {
  try {
    const res = await lineMsgTemplateService.getPresets();
    if (res?.success && res.data) {
      allPresets.value = res.data;
      presetOptions.value = [
        ...res.data.map(p => ({ key: p.key, name: p.name })),
        { key: 'custom', name: '自訂 JSON' }
      ];
    }
  } catch (e) { /* ignore */ }
});

function getSchema(presetKey) {
  const p = allPresets.value.find(x => x.key === presetKey);
  return p ? p.schema : [];
}

function setBubblePreset(idx, presetKey) {
  const b = bubbles.value[idx];
  if (b.preset === presetKey) return;
  const defaults = {};
  if (presetKey !== 'custom') {
    const p = allPresets.value.find(x => x.key === presetKey);
    if (p) p.schema.forEach(f => { if (f.default !== undefined && f.default !== '') defaults[f.key] = f.default; });
  }
  bubbles.value[idx] = {
    preset: presetKey,
    fields: presetKey !== 'custom' ? defaults : {},
    json: presetKey === 'custom' ? (b.json || '') : ''
  };
  emitChange();
}

function updateBubbleFields(idx, fields) {
  bubbles.value[idx] = { ...bubbles.value[idx], fields };
  emitChange();
}

function updateBubbleJson(idx, text) {
  const b = { ...bubbles.value[idx], json: text, _jsonError: '' };
  if (text.trim()) {
    try {
      JSON.parse(text);
    } catch (e) {
      b._jsonError = 'JSON 格式錯誤：' + e.message;
    }
  }
  bubbles.value[idx] = b;
  emitChange();
}

function addBubble() {
  // Default to the same preset as the last bubble for convenience
  const lastPreset = bubbles.value.length > 0 ? bubbles.value[bubbles.value.length - 1].preset : '';
  bubbles.value.push({ preset: lastPreset, fields: {} });
  expanded.value.push(true);
  emitChange();
}

function removeBubble(idx) {
  if (bubbles.value.length <= 1) return;
  bubbles.value.splice(idx, 1);
  expanded.value.splice(idx, 1);
  emitChange();
}

function moveBubble(idx, direction) {
  const target = idx + direction;
  if (target < 0 || target >= bubbles.value.length) return;
  const arr = [...bubbles.value];
  [arr[idx], arr[target]] = [arr[target], arr[idx]];
  bubbles.value = arr;
  const ex = [...expanded.value];
  [ex[idx], ex[target]] = [ex[target], ex[idx]];
  expanded.value = ex;
  emitChange();
}

function emitChange() {
  const cleanBubbles = bubbles.value.map(b => {
    const item = { preset: b.preset, fields: b.fields || {} };
    if (b.preset === 'custom' && b.json) item.json = b.json;
    return item;
  });
  emit('update:modelValue', {
    ...props.modelValue,
    flexPreset: 'carousel_mixed',
    templateFields: cleanBubbles
  });
}
</script>

<style scoped>
.section-desc { color: #6b7280; font-size: 13px; margin-bottom: 16px; }
.bubbles-list { display: flex; flex-direction: column; gap: 16px; }

.bubble-card {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
}
.bubble-card.is-collapsed {
  padding-bottom: 12px;
}
.bubble-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.bubble-card.is-collapsed .bubble-header {
  margin-bottom: 0;
}
.bubble-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.collapse-toggle {
  flex-shrink: 0;
}
.bubble-title {
  font-weight: 600;
  font-size: 14px;
  color: #374151;
}
.bubble-actions {
  display: flex;
  gap: 4px;
}

.icon-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 14px;
  transition: all .15s;
}
.icon-btn:hover:not(:disabled) { border-color: #93c5fd; color: #3b82f6; }
.icon-btn.danger:hover:not(:disabled) { border-color: #ef4444; color: #ef4444; }
.icon-btn:disabled { opacity: .35; cursor: not-allowed; }

/* Preset chips */
.bubble-preset-selector {
  margin-bottom: 14px;
}
.bubble-preset-selector .form-label {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
  display: block;
}
.preset-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.preset-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border: 1px solid #d1d5db;
  border-radius: 20px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #6b7280;
  transition: all .15s;
}
.preset-chip i { font-size: 14px; }
.preset-chip:hover { border-color: #93c5fd; color: #3b82f6; }
.preset-chip.active {
  border-color: #3b82f6;
  background: #eff6ff;
  color: #3b82f6;
  font-weight: 500;
}

.custom-json { margin-top: 4px; }
.form-label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}
.required-label::after { content: ' *'; color: #ef4444; }
.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}
.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,.1);
}
.code-textarea {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  resize: vertical;
  min-height: 140px;
}
.error-text { display: block; font-size: 12px; color: #ef4444; margin-top: 4px; }

.btn-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
  cursor: pointer;
}
.btn-add:hover { border-color: #3b82f6; color: #3b82f6; }
</style>
